/**
 * Admin booking search: field detection plus ranked, tolerant matching.
 */

export const BOOKING_SEARCH_LOOKBACK_MONTHS = 24;
export const BOOKING_SEARCH_MIN_CHARS = 2;
export const BOOKING_SEARCH_MAX_RESULTS = 100;

/**
 * Detect whether a query should search by phone, email, or name.
 * Handles Irish-style numbers: +353…, 087…, spaces, hyphens.
 *
 * @param {string|null|undefined} query
 * @returns {"phoneNumber"|"emailAddress"|"name"}
 */
export function detectBookingSearchField(query) {
  const value = (query == null ? "" : String(query)).trim();
  if (!value) return "name";

  if (value.includes("@")) {
    return "emailAddress";
  }

  // Digits only (ignore common phone punctuation / spaces)
  const digits = value.replace(/[\s\-().+]/g, "");
  if (digits.length >= 6 && /^\d+$/.test(digits)) {
    return "phoneNumber";
  }

  return "name";
}

/**
 * Safely extract error message from Firebase / HttpsError / strings.
 * @param {unknown} error
 * @param {string} [fallback]
 */
export function errorMessage(error, fallback = "Something went wrong") {
  if (!error) return fallback;
  if (typeof error === "string") return error || fallback;
  if (error.message) return String(error.message);
  if (error.details) return String(error.details);
  return fallback;
}

/**
 * Earliest non-null opening time from a shop openingTimes map.
 * @param {Record<string, string|null|undefined>|null|undefined} openingTimes
 * @returns {string|null}
 */
export function earliestOpeningTime(openingTimes) {
  if (!openingTimes || typeof openingTimes !== "object") return null;
  const vals = Object.values(openingTimes).filter(
    v => typeof v === "string" && v.length > 0
  );
  if (!vals.length) return null;
  return vals.slice().sort()[0];
}

/**
 * Latest non-null closing time from a shop closingTimes map.
 * @param {Record<string, string|null|undefined>|null|undefined} closingTimes
 * @returns {string|null}
 */
export function latestClosingTime(closingTimes) {
  if (!closingTimes || typeof closingTimes !== "object") return null;
  const vals = Object.values(closingTimes).filter(
    v => typeof v === "string" && v.length > 0
  );
  if (!vals.length) return null;
  return vals.slice().sort().reverse()[0];
}

/**
 * Locale-aware employee name sort (null-safe).
 */
export function compareEmployeeNames(a, b) {
  const an = (a && a.name) || "";
  const bn = (b && b.name) || "";
  return an.localeCompare(bn, undefined, { sensitivity: "base" });
}

/**
 * Fold accents, case, and punctuation so "Siobhán" / "O'Brien" still match.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeSearchText(value) {
  return String(value == null ? "" : value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[''`´]/g, "")
    .replace(/[^a-z0-9@+.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function digitsOnly(value) {
  return String(value == null ? "" : value).replace(/\D/g, "");
}

/**
 * Irish mobile/landline variants: +35387…, 0035387…, 087…, 87…
 * @param {unknown} value
 * @returns {string[]}
 */
export function irishPhoneVariants(value) {
  const digits = digitsOnly(value);
  if (!digits) return [];
  const variants = new Set([digits]);

  if (digits.indexOf("353") === 0 && digits.length > 3) {
    const rest = digits.slice(3);
    variants.add("0" + rest);
    variants.add(rest);
  }

  if (digits.indexOf("00") === 0 && digits.slice(2, 5) === "353") {
    const rest = digits.slice(5);
    variants.add("353" + rest);
    variants.add("0" + rest);
    variants.add(rest);
  }

  if (digits.charAt(0) === "0" && digits.length > 1) {
    const rest = digits.slice(1);
    variants.add("353" + rest);
    variants.add(rest);
  }

  return Array.from(variants);
}

/**
 * @param {unknown} query
 * @param {unknown} phone
 * @returns {{ match: boolean, score: number }}
 */
export function phoneQueryMatches(query, phone) {
  const qDigits = digitsOnly(query);
  if (qDigits.length < 4) {
    return { match: false, score: 0 };
  }

  const queryVariants = irishPhoneVariants(query);
  const phoneVariants = irishPhoneVariants(phone);
  if (!queryVariants.length || !phoneVariants.length) {
    return { match: false, score: 0 };
  }

  let best = 0;
  for (let i = 0; i < queryVariants.length; i += 1) {
    const q = queryVariants[i];
    for (let j = 0; j < phoneVariants.length; j += 1) {
      const p = phoneVariants[j];
      if (p === q) {
        best = Math.max(best, 100);
      } else if (q.length >= 4 && p.indexOf(q) !== -1) {
        best = Math.max(best, Math.min(96, 78 + q.length));
      } else if (p.length >= 6 && q.indexOf(p) !== -1) {
        best = Math.max(best, 72);
      }
    }
  }

  return best > 0 ? { match: true, score: best } : { match: false, score: 0 };
}

/**
 * Damerau–Levenshtein (insert, delete, substitute, adjacent transpose).
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function editDistance(a, b) {
  const left = String(a || "");
  const right = String(b || "");
  const al = left.length;
  const bl = right.length;
  if (!al) return bl;
  if (!bl) return al;

  const d = [];
  for (let i = 0; i <= al; i += 1) {
    d[i] = new Array(bl + 1);
    d[i][0] = i;
  }
  for (let j = 0; j <= bl; j += 1) {
    d[0][j] = j;
  }

  for (let i = 1; i <= al; i += 1) {
    for (let j = 1; j <= bl; j += 1) {
      const cost = left.charAt(i - 1) === right.charAt(j - 1) ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (
        i > 1 &&
        j > 1 &&
        left.charAt(i - 1) === right.charAt(j - 2) &&
        left.charAt(i - 2) === right.charAt(j - 1)
      ) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[al][bl];
}

function allowedEdits(length) {
  if (length < 4) return 0;
  if (length < 7) return 1;
  return 2;
}

function tokenMatch(queryToken, candidate) {
  if (!queryToken || !candidate) return null;
  if (candidate === queryToken) return "exact";
  if (candidate.indexOf(queryToken) === 0) return "prefix";
  if (queryToken.length >= 3 && candidate.indexOf(queryToken) !== -1) {
    return "contains";
  }
  const limit = allowedEdits(Math.max(queryToken.length, candidate.length));
  if (limit > 0 && editDistance(queryToken, candidate) <= limit) {
    return "fuzzy";
  }
  return null;
}

const TOKEN_KIND_SCORE = {
  exact: 30,
  prefix: 24,
  contains: 20,
  fuzzy: 16
};

function bestTokenScore(queryToken, candidates) {
  let best = 0;
  for (let i = 0; i < candidates.length; i += 1) {
    const kind = tokenMatch(queryToken, candidates[i]);
    if (kind) best = Math.max(best, TOKEN_KIND_SCORE[kind]);
  }
  return best;
}

function addMatch(matched, key) {
  if (matched.indexOf(key) === -1) matched.push(key);
}

/**
 * Attach staff / service labels used by the ranker and result rows.
 * @param {Array<Object>} records
 * @param {{ employeeById?: Function, serviceById?: Function }} [lookups]
 */
export function withSearchLookups(records, lookups) {
  const list = Array.isArray(records) ? records : [];
  const employeeById = (lookups && lookups.employeeById) || function() {};
  const serviceById = (lookups && lookups.serviceById) || function() {};

  return list.map(record => {
    const employee = record.resourceId ? employeeById(record.resourceId) : null;
    const service = record.serviceId ? serviceById(record.serviceId) : null;
    let packageName = "";
    if (service && record.servicePackageId && service.packages) {
      const pkg = service.packages[record.servicePackageId];
      packageName = (pkg && pkg.name) || "";
    }
    return {
      ...record,
      employeeName: (employee && employee.name) || record.employeeName || "",
      serviceName: (service && service.name) || record.serviceName || "",
      packageName: packageName || record.packageName || ""
    };
  });
}

function scoreTextField(normalizedQuery, normalizedField) {
  if (!normalizedQuery || !normalizedField) return 0;
  if (normalizedField === normalizedQuery) return 100;
  if (normalizedField.indexOf(normalizedQuery) === 0) return 92;
  if (normalizedQuery.length >= 2 && normalizedField.indexOf(normalizedQuery) !== -1) {
    return 80;
  }
  return 0;
}

function scoreBookingRecord(record, query, options) {
  const name = normalizeSearchText(record.name);
  const email = normalizeSearchText(record.email);
  const notes = normalizeSearchText(record.notes);
  const staff = normalizeSearchText(record.employeeName);
  const service = normalizeSearchText(
    [record.serviceName, record.packageName].filter(Boolean).join(" ")
  );
  const id = normalizeSearchText(record._id);
  const nameTokens = name.split(" ").filter(Boolean);
  const staffTokens = staff.split(" ").filter(Boolean);
  const serviceTokens = service.split(" ").filter(Boolean);
  const emailLocal = email.split("@")[0] || "";

  const isPhoneOnly =
    query.digits.length >= 6 && !/[a-z]/i.test(query.raw.replace(/[\s\-().+]/g, ""));
  const isEmailish = query.raw.indexOf("@") !== -1;

  let score = 0;
  const matched = [];

  const phone = phoneQueryMatches(query.raw, record.number);
  if (phone.match) {
    score = Math.max(score, phone.score);
    addMatch(matched, "phone");
  }

  if (!isPhoneOnly) {
    const nameScore = scoreTextField(query.text, name);
    if (nameScore) {
      score = Math.max(score, nameScore);
      addMatch(matched, "name");
    } else if (query.tokens.length) {
      const tokenScores = query.tokens.map(token =>
        bestTokenScore(token, nameTokens.concat(emailLocal ? [emailLocal] : []))
      );
      if (tokenScores.length && tokenScores.every(value => value > 0)) {
        const tokenTotal = tokenScores.reduce((sum, value) => sum + value, 0);
        score = Math.max(score, Math.min(90, 38 + tokenTotal));
        addMatch(matched, "name");
      }
    }

    const emailScore = scoreTextField(query.text, email);
    if (emailScore) {
      score = Math.max(score, isEmailish ? emailScore : Math.min(emailScore, 86));
      addMatch(matched, "email");
    } else if (emailLocal && query.text && emailLocal.indexOf(query.text) !== -1) {
      score = Math.max(score, 74);
      addMatch(matched, "email");
    }

    const staffScore = scoreTextField(query.text, staff);
    if (staffScore) {
      score = Math.max(score, Math.min(staffScore, 78));
      addMatch(matched, "staff");
    } else if (query.tokens.length && staffTokens.length) {
      const tokenScores = query.tokens.map(token =>
        bestTokenScore(token, staffTokens)
      );
      if (tokenScores.length && tokenScores.every(value => value > 0)) {
        score = Math.max(score, 62);
        addMatch(matched, "staff");
      }
    }

    const serviceScore = scoreTextField(query.text, service);
    if (serviceScore) {
      score = Math.max(score, Math.min(serviceScore, 74));
      addMatch(matched, "service");
    } else if (query.tokens.length && serviceTokens.length) {
      const tokenScores = query.tokens.map(token =>
        bestTokenScore(token, serviceTokens)
      );
      if (tokenScores.length && tokenScores.every(value => value > 0)) {
        score = Math.max(score, 58);
        addMatch(matched, "service");
      }
    }

    if (notes && query.text.length >= 3 && notes.indexOf(query.text) !== -1) {
      score = Math.max(score, 42);
      addMatch(matched, "notes");
    }
  }

  if (id && query.text.length >= 4 && (id === query.text || id.indexOf(query.text) === 0)) {
    score = Math.max(score, 88);
    addMatch(matched, "id");
  }

  if (!matched.length) return null;

  const preferField = options.preferField || detectBookingSearchField(query.raw);
  if (preferField === "emailAddress" && matched.indexOf("email") !== -1) score += 10;
  if (preferField === "phoneNumber" && matched.indexOf("phone") !== -1) score += 10;
  if (preferField === "name" && matched.indexOf("name") !== -1) score += 6;

  const now = options.now != null ? options.now : Date.now();
  const start = record.startMs || 0;
  const day = 24 * 60 * 60 * 1000;
  if (start >= now && start <= now + 14 * day) score += 8;
  else if (start < now && start >= now - 14 * day) score += 4;

  return {
    ...record,
    score,
    matchedIn: matched
  };
}

/**
 * Rank bookings for a free-text query. Partial names, last names, typos,
 * emails, Irish phone formats, staff, and service all count.
 *
 * @param {Array<Object>} records
 * @param {string} query
 * @param {{ minChars?: number, limit?: number, now?: number, preferField?: string }} [options]
 * @returns {Array<Object>}
 */
export function rankBookingSearch(records, query, options) {
  const opts = options || {};
  const raw = (query == null ? "" : String(query)).trim();
  const minChars = opts.minChars != null ? opts.minChars : BOOKING_SEARCH_MIN_CHARS;
  if (raw.length < minChars) return [];

  const text = normalizeSearchText(raw);
  const parsed = {
    raw,
    text,
    tokens: text.split(" ").filter(token => token.length >= 2),
    digits: digitsOnly(raw)
  };

  // Phone-only queries can be shorter than the text minimum once punctuation
  // is stripped, but still need enough digits to be useful.
  if (!parsed.text && parsed.digits.length < 4) return [];

  const list = Array.isArray(records) ? records : [];
  const scored = [];
  for (let i = 0; i < list.length; i += 1) {
    const hit = scoreBookingRecord(list[i], parsed, opts);
    if (hit) scored.push(hit);
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (b.startMs || 0) - (a.startMs || 0);
  });

  const limit = opts.limit != null ? opts.limit : BOOKING_SEARCH_MAX_RESULTS;
  return scored.slice(0, limit);
}

export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Wrap query tokens in <mark> for result highlighting.
 * @param {unknown} text
 * @param {unknown} query
 * @returns {string}
 */
export function highlightSearchText(text, query) {
  const source = String(text == null ? "" : text);
  if (!source) return "";
  const raw = String(query == null ? "" : query).trim();
  if (!raw) return escapeHtml(source);

  const tokens = [];
  raw.split(/[\s,;]+/).forEach(part => {
    if (!part) return;
    if (part.length >= 2) tokens.push(part);
    const digits = part.replace(/\D/g, "");
    if (digits.length >= 3 && tokens.indexOf(digits) === -1) tokens.push(digits);
  });
  if (!tokens.length) return escapeHtml(source);

  const unique = [];
  tokens.forEach(token => {
    const key = token.toLowerCase();
    if (!unique.some(existing => existing.toLowerCase() === key)) {
      unique.push(token);
    }
  });

  const re = new RegExp(`(${unique.map(escapeRegExp).join("|")})`, "ig");
  const parts = source.split(re);
  return parts
    .map((part, index) => {
      if (!part) return "";
      if (index % 2 === 1) return `<mark>${escapeHtml(part)}</mark>`;
      return escapeHtml(part);
    })
    .join("");
}

const MATCH_LABELS = {
  name: "name",
  email: "email",
  phone: "phone",
  notes: "notes",
  id: "reference",
  staff: "staff",
  service: "service"
};

export function formatMatchedIn(matchedIn) {
  const labels = (matchedIn || [])
    .map(key => MATCH_LABELS[key] || key)
    .filter(Boolean);
  if (!labels.length) return "";
  if (labels.length === 1) return `Matched on ${labels[0]}`;
  if (labels.length === 2) return `Matched on ${labels[0]} and ${labels[1]}`;
  return `Matched on ${labels.slice(0, -1).join(", ")} and ${
    labels[labels.length - 1]
  }`;
}
